---
title: Prompt transparency (internal)
summary: How the composed-prompt inspector, SYSTEM LAYERS review digest, and turn-exact prompt capture fit together.
visibility: internal
audience: author-admin
section: Internal
order: 900
last_verified: 2026-07-22
owner: docs
---

# Prompt transparency

A persona's authored blocks (system prompt, skills, examples) are never the whole
story: the engine adds its own runtime prose on top of them at render time, and
for workflow turns it adds a further layer that only exists inside that turn's
decide loop. Three read-only surfaces exist so maintainers can see what a persona
actually receives instead of guessing or hallucinating it.

## One registry, three surfaces

`backend/app/services/prompt_inventory.py` is the single source of truth for
engine-owned prompt text — every entry imports the live constant it documents
(never a copy), so the registry cannot drift from the code that actually
renders. Static entries hold the literal text; per-turn-dynamic entries
(facts lists, agenda) hold their instruction *template* with placeholders,
since the rule is static even where the data isn't.

That one registry feeds:

1. **The composed-prompt inspector** (Workshop → Advanced → View composed
   prompt) — its "System layers" tab renders the registry grouped by stage,
   with source refs, alongside the persona's own authored blocks.
2. **The SYSTEM LAYERS digest** appended to both persona-review corpora
   (persona and workflow_fit reviews) — so a review can cite real engine
   prose (`layer_id`, verbatim quote required) instead of inventing a rule
   that doesn't exist anywhere. Reviews may cite these entries; they may
   never propose edits to them — the layers aren't authored content.
3. **`GET /api/prompt-inventory`** — the same registry, served directly, for
   anything that wants the raw list.

This page only describes how the pieces relate. The registry and the endpoint
are the source of truth for what the layers actually say — no rule text is
duplicated here, and none should be added.

## The inspector's two conversation modes

The inspector's "Rendered for" picker has two modes:

- **Saved bundle only** — the default: renders the persona's authored blocks
  as saved, with no conversation attached.
- **Conversation (reconstruction)** — attaches a real conversation so the
  runtime layers that depend on conversation state render with real content
  instead of empty name-only chips. This is honestly labeled a
  *reconstruction*: it uses production-equivalent turn signals and the
  conversation's last customer message, but for workflow-bound conversations
  it cannot include the workflow-turn context (guardrails, facts, agenda)
  that the controller's per-turn decide loop assembles — that context is a
  function of state that no longer exists once the turn has passed, so it
  cannot be faithfully recomputed after the fact.

## Turn-exact capture

Because reconstruction can't be exact for workflow turns, a fourth surface
exists: **turn-exact prompt capture**. Rather than
reconstructing, it persists the actual composed system prompt at the moment
each agent turn renders it, so it can be replayed byte-for-byte later. Key
properties:

- **Runtime-gated** — an environment default (`PROMPT_CAPTURE_ENABLED`,
  **staging-on / prod-off** until table growth has been observed) that an
  admin setting overrides at runtime without a redeploy (Admin → App
  settings → Prompt capture).
- **Admin-permission-gated reads** — captured snapshots embed customer
  context beyond what a persona's own authored text contains, so read access
  is admin-only, not merely persona-owner.
- **30-day retention** (`PROMPT_CAPTURE_RETENTION_DAYS`) — captured rows
  are swept by a background retention loop; capture is not a permanent
  record.
- **Fire-and-forget** — a capture failure never fails the turn it's
  attached to.

The inspector's conversation mode shows the reconstruction above by default
(with its honesty label intact). Once a conversation is selected, a turn
picker (admin-permission-gated, and only shown when at least one captured
snapshot exists for that conversation) lets an admin open a specific turn's
captured snapshot instead — an explicit, per-turn opt-in, not an automatic
preference.

## Why this page is internal-only

This page is `visibility: internal`. Internal-tier pages render on no
user-facing surface — not `/help`, not the docs corpus, not the public docs
site — by design. That's deliberate here: these are engine-owned, read-only
mechanics for maintainers debugging or reviewing persona behavior, not
something an end user or even a persona author configures. This file is
repo-resident maintainer documentation, kept in the same MDX source and
frontmatter system as the rest of `/help` purely for consistency, not because
it's meant to be read in-product.

## See also

- `backend/app/services/prompt_inventory.py` — the registry.
- `GET /api/prompt-inventory` — the served endpoint.
- Workshop → Advanced → View composed prompt — the inspector.
